import type { PublishingAccount } from '../../types/publishing.js';
import type { MenuGroupMode } from './calendarSelection.js';

// Publishing calendar — the left-menu grouping (2026-07-29). PURE reorganisation
// of the postable accounts for browsing/selection: under marque headers, under
// owner-group headers, or a flat alphabetical list. It is a DISPLAY control only —
// it never filters the calendar, colours chips, or changes the grid.
//
// brandName / groupName are resolved SERVER-SIDE on PublishingAccount (the same
// computeBrandAncestorMap / computeGroupAncestorMap used everywhere), so this stays
// a trivial pure group-by.

export interface AccountMenuGroup {
  // Stable key for React + tests.
  key: string;
  // Header label; null = a flat list with no header (the 'flat' mode).
  label: string | null;
  accounts: PublishingAccount[];
}

// The "no brand" / "no group" bucket sorts LAST; everything else alphabetically.
function compareGroupLabels(a: string, b: string, unassigned: string): number {
  if (a === unassigned && b !== unassigned) return 1;
  if (b === unassigned && a !== unassigned) return -1;
  return a.localeCompare(b);
}

function byName(a: PublishingAccount, b: PublishingAccount): number {
  return a.name.localeCompare(b.name);
}

/**
 * Group the postable accounts for the left menu.
 * - 'flat'  → ONE group (label null), accounts alphabetical.
 * - 'brand' → grouped by brandName ("No brand" bucket last).
 * - 'group' → grouped by groupName ("No group" bucket last).
 */
export function groupAccountsForMenu(
  accounts: PublishingAccount[],
  mode: MenuGroupMode,
): AccountMenuGroup[] {
  if (mode === 'flat') {
    return [{ key: 'all', label: null, accounts: [...accounts].sort(byName) }];
  }
  const unassigned = mode === 'brand' ? 'No brand' : 'No group';
  const labelOf = (a: PublishingAccount) =>
    (mode === 'brand' ? a.brandName : a.groupName)?.trim() || unassigned;

  const byLabel = new Map<string, PublishingAccount[]>();
  for (const a of accounts) {
    const label = labelOf(a);
    const bucket = byLabel.get(label) ?? [];
    bucket.push(a);
    byLabel.set(label, bucket);
  }
  return [...byLabel.entries()]
    .map(([label, items]) => ({ key: label, label, accounts: items.sort(byName) }))
    .sort((x, y) => compareGroupLabels(x.label, y.label, unassigned));
}
